import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import ProductTile from 'components/Catalog/ProductGrid/ProductTile';
import MarketingTile from 'components/Catalog/ProductGrid/MarketingTile';
import LazyLoad from 'components/LazyLoad';
import CompareDrawer from 'components/Catalog/ProductGrid/CompareDrawer';
import { useCompareState } from 'components/Catalog/ProductGrid/hooks/useCompareProducts';
import ProductCompareActions from 'actions/ProductCompareActions';
import {
    Flex, Grid, Box, Text, Button, Icon
} from 'components/ui';
import InputSwitch from 'components/Inputs/InputSwitch/InputSwitch';
import getStyles from 'components/Catalog/ProductGrid/styles';
import localUtils from 'utils/LanguageLocale';
import { mediaQueries, space, lineHeights } from 'style/config';

import userUtils from 'utils/User';
import anaConsts from 'analytics/constants';
import processEvent from 'analytics/processEvent';
import dateUtils from 'utils/Date';
import ProductSort from 'components/Catalog/ProductSort';
import FilterChiclets from 'components/Catalog/Filters/FilterChiclets';
import mediaUtils from 'utils/Media';
import UI from 'utils/UI';
import VisuallyHidden from 'components/VisuallyHidden';
const { isFrench } = localUtils;
const { Media } = mediaUtils;
import { REFINEMENT_TYPES } from '@ufe/shared/constants/catalog';
import catalogUtils from 'utils/Catalog';
import cookieUtils from 'utils/Cookies';
import { SKIN_ANALYSIS_TOOL } from 'constants/arSkincare';
import ProductListPage from 'ai/components/EntryPoint/ProductListPage';
import isRmnCombinedCallFeatureEnabled from 'components/Rmn/utils';
import RmnMidPageBanner from 'components/Rmn/RmnMidPageBanner';
import rmnAndPla from 'utils/rmnAndPla';
import helpersUtils from 'utils/Helpers';
import StringUtils from 'utils/String';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';
import SmartSkinScanBanner from 'components/Catalog/ProductGrid/SmartSkinScanBanner/SmartSkinScanBanner.f';
import { SSS_PLACEMENTS, SSS_OFFSET_FROM_AIBC } from 'constants/smartSkinScan';
import { showSelector as purchasedTimesSRPShowSelector } from 'selectors/testTarget/offers/purchasedTimesSRP/show/showSelector';
import RelatedSearches from 'components/RelatedSearches';
import { relatedSearchesVariantSelector } from 'viewModel/selectors/testTarget/relatedSearchesVariantSelector';
import { relatedSearchTermsSelector } from 'selectors/page/relatedSearch/relatedSearchTermsSelector';
import { isRelatedSearchesEligible } from 'components/RelatedSearches/constants';

const { deferTaskExecution } = helpersUtils;

const SERVER_RENDER_LIMIT = 6;
const GRID_TYPES = {
    MID_PAGE_BANNER: 'midPageBanner',
    MARKETING_TILE: 'marketingTile',
    PRODUCT_TILE: 'productTile',
    AI_PRODUCT_CHAT: 'aiProductChat',
    SMART_SKIN_SCAN: 'smartSkinScan',
    RELATED_SEARCHES: 'relatedSearches'
};
// Page source types for compare feature visibility
const PAGE_SOURCE = {
    CATEGORY: 'category',
    SEARCH: 'search',
    BRAND: 'brand'
};
const MID_BANNER_DEFAULT_POSITION = 12;
const AI_PRODUCT_CHAT_POSITION = Sephora.configurationSettings.isGenAIPLPHigherPlacement ? 4 : 10;
// Related Searches inserts after the 2nd product row: flat index = 2 * columns.
const RELATED_SEARCHES_POSITION_SM = 4; // after row 2 @ 2 cols (SMUI, <556px)
const RELATED_SEARCHES_POSITION_LG = 6; // after row 2 @ 3 cols (LGUI, >=556px)

// The default slots for the Content Grid Tiles should be as follows:
// P8, P17, P36, P49, P59
// https://jira.sephora.com/browse/SMC-2093
const DEFAULT_MARKETING_TILE_SLOTS = [8, 17, 36, 49, 59];
const { isNDDNewDeliveryName } = DeliveryOptionsUtils;

export const MarketingTileComponent = React.memo(props => {
    const { tile, index, count, increaseServerRenderedMarketingTiles } = props;
    const tileStyles = getStyles();
    const order = index;
    const isIllinoisUser = cookieUtils.read(cookieUtils.KEYS.ILLINOIS_YES) === 'true';
    const isSkinAnalysisTool = tile?.action?.page?.slug === SKIN_ANALYSIS_TOOL;

    if (isIllinoisUser && isSkinAnalysisTool) {
        return null;
    }

    // The order is used to determine the position of the tile in the grid.
    if (order === undefined || isNaN(order)) {
        return null;
    }

    const isLazyLoaded = order >= SERVER_RENDER_LIMIT && count > SERVER_RENDER_LIMIT;

    if (!isLazyLoaded) {
        increaseServerRenderedMarketingTiles();
    }

    const Component = isLazyLoaded ? LazyLoad : MarketingTile;

    // Analytics Data.
    tile['slot'] = order;
    tile['isLazyLoaded'] = isLazyLoaded;

    const orderStyle = !isNaN(order) ? order : undefined;

    return (
        <div
            key={`marketingTile_${tile.name || tile.sid}`}
            css={[tileStyles.item, isLazyLoaded && tileStyles.itemLazy]}
            style={{ order: orderStyle, alignSelf: 'flex-start' }}
        >
            <Component
                {...(isLazyLoaded && {
                    isLazyLoaded: true,
                    component: MarketingTile
                })}
                content={tile}
            />
        </div>
    );
});

const BannerWrapper = React.memo(props => {
    const { position, children, styles: bannerStyles } = props;
    const styles = getStyles();

    const override = {
        order: position,
        height: 'auto',
        minHeight: 'auto',
        width: '100%'
    };

    return (
        <div
            key={`banner_${position}`}
            css={[styles.banner, bannerStyles]}
            style={override}
        >
            {children}
        </div>
    );
});

export const BannerComponent = React.memo(props => {
    const {
        showMidPageBanner, source, position, styles, rmnBannerPayload, fallbackBannerPayload, midPageBannerProps
    } = props;

    // Kill switch for mid page banner
    const hiddenMidPageBanner = !Sephora?.configurationSettings?.enableMidPageBanner;

    if (hiddenMidPageBanner || !showMidPageBanner) {
        return null;
    }

    const isLazyLoaded = position >= SERVER_RENDER_LIMIT && props.count > SERVER_RENDER_LIMIT;

    if (!isLazyLoaded) {
        props.increaseServerRenderedMarketingTiles();
    }

    if (isRmnCombinedCallFeatureEnabled()) {
        if (!Object.keys(rmnBannerPayload)?.length) {
            return null;
        }

        return (
            <BannerWrapper
                position={position}
                styles={styles}
            >
                <RmnMidPageBanner
                    position={position}
                    pageType={source}
                    rmnBannerPayload={rmnBannerPayload}
                    fallbackBannerPayload={fallbackBannerPayload}
                    bannerProps={midPageBannerProps}
                />
            </BannerWrapper>
        );
    }

    return null;
});

function ProductGrid(props) {
    const {
        h1,
        products = [],
        appliedFilters,
        refinements,
        selectFilters,
        removeFilterValue,
        clearAllFilters,
        source,
        searchWarningMessages,
        searchQuery,
        deliveryOptions,
        pageType,
        sponsorProducts = [],
        sponsorProductsLoaded,
        showSponsorProducts = false,
        marketingTiles = [],
        resultsText,
        dontShowMoreProducts,
        currentTotalText,
        categoryName,
        parentCategoryName,
        showUpdateDefaultSort,
        isGenAIChatPLPEnabled,
        giftFinder,
        totalProducts,
        addItemCnstrcData,
        isSearch,
        resultId,
        isBrowse,
        constructorFilters,
        showPurchasedFlagOnGridPages,
        currentPage = 1,
        setPageNumber,
        setResultsCount,
        layoutWrapRef,
        pageName,
        fetchCompletePurchaseHistory,
        isBrand,
        showMidPageBanner,
        marketingTilePositions = [],
        locales,
        smartSkinScanBannerConfig,
        showSMNEnabled = true,
        showPurchasedTimesSRP,
        showPlpProductCompare,
        simplifyFiltersOnPLP
    } = props;

    // State
    const [isLoading, setIsLoading] = useState(false);
    const [prevContextId, setPrevContextId] = useState(props.contextId);
    const [apiCalled, setApiCalled] = useState(false);
    const { isCompareModeEnabled, selectedProducts } = useCompareState();
    const dispatch = useDispatch();
    const isPurchaseTestConfigured = useSelector(purchasedTimesSRPShowSelector);
    const profileId = useSelector(state => state.user?.profileId);
    // Single visibility source: the grid reservation gate reads the SAME selectors the shell renders on.
    const relatedSearchesVariant = useSelector(relatedSearchesVariantSelector);
    const relatedSearchTerms = useSelector(relatedSearchTermsSelector, shallowEqual);
    const isPlpComparisonToolEnabled = Sephora.configurationSettings?.plpComparisonTool?.isEnabled === true;
    const isCompareFeatureEnabled = isPlpComparisonToolEnabled && showPlpProductCompare;
    const {
        compare,
        compareTool,
        resultsFor,
        SDDUnavailable,
        SDDUnavailable2,
        noSDDResult,
        noResults1,
        noSDDResult2,
        noResults2,
        showMoreProducts,
        noNDDResult,
        NDDUnavailable
    } = locales;

    // Refs
    const rootRef = useRef(null);
    const serverRenderedMarketingTilesRef = useRef(0);
    const prevPageRef = useRef(currentPage);
    const prevCategoryNameRef = useRef(categoryName); // Initialize with current category
    const prevCategoryNameForPlaRef = useRef(categoryName); // Separate ref for PLA tracking
    const firedViewableImpressionIdsRef = useRef(new Set());
    const hasFiredPlaOnLoadRef = useRef(false); // Track if PLA onload event has fired

    // getDerivedStateFromProps equivalent
    useEffect(() => {
        if (props.contextId !== prevContextId) {
            setPrevContextId(props.contextId);

            if (prevContextId != null && props.contextId != null) {
                firedViewableImpressionIdsRef.current.clear();
            }
        }
    }, [props.contextId, prevContextId]);

    // Reset serverRenderedMarketingTilesRef on SPA navigation
    useEffect(() => {
        const categoryChanged = categoryName !== prevCategoryNameRef.current;
        const pageChanged = currentPage !== prevPageRef.current;

        if (categoryChanged || (pageChanged && currentPage === 1)) {
            serverRenderedMarketingTilesRef.current = 0;

            prevCategoryNameRef.current = categoryName;
            prevPageRef.current = currentPage;
        }
    }, [categoryName, currentPage]);

    // loadPurchaseHistory function
    const loadPurchaseHistory = () => {
        if (profileId) {
            fetchCompletePurchaseHistory(
                { userId: profileId },
                {
                    useIndexedDB: true,
                    cacheKey: isPurchaseTestConfigured ? `purchaseHistory_${profileId}_1000` : `purchaseHistory_${profileId}`,
                    ...(isPurchaseTestConfigured && { itemsPerPage: 1000 })
                }
            );
        }
    };

    // handleLoadMore function
    const handleLoadMore = () => {
        deferTaskExecution(() => {
            setIsLoading(true);
            // Use setTimeout to ensure state is set before calling setPageNumber
            setTimeout(() => {
                setPageNumber(currentPage + 1);
            }, 0);

            // Analytics - EXP-1358
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    linkName: 'D=c55',
                    actionInfo: anaConsts.LinkData.SHOW_MORE_PRODUCTS,
                    eventStrings: [anaConsts.Event.EVENT_71]
                }
            });
        });
    };

    // renderResultsText function
    const renderResultsText = ({ resultsText: resultTextProp, searchQuery: searchQueryProp, pageType: pageTypeProp }) => {
        return (
            <Text
                is='p'
                lineHeight='tight'
                color='gray'
                fontSize={['sm', 'base']}
                data-at={Sephora.debug.dataAt('number_of_products')}
            >
                {resultTextProp}
                {pageTypeProp === 'searchResults' && searchQueryProp && (
                    <>
                        {` ${resultsFor} `}
                        <Text
                            is='span'
                            color='black'
                            fontWeight='bold'
                            children={isFrench() ? `« ${searchQueryProp} »` : `"${searchQueryProp}"`}
                        />
                    </>
                )}
            </Text>
        );
    };

    // handleCompareToggle function
    const handleCompareToggle = () => {
        const nextCompareModeEnabled = !isCompareModeEnabled;

        if (nextCompareModeEnabled) {
            processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
                data: {
                    eventStrings: [anaConsts.Event.EVENT_71],
                    linkName: 'D=c55',
                    actionInfo: anaConsts.ACTION_INFO.COMPARE_PRODUCT_TOGGLE_ON // prop55
                }
            });
        }

        dispatch(ProductCompareActions.setCompareMode(nextCompareModeEnabled));
    };

    // handleProductToggle function
    const handleProductToggle = (product, isSelected) => {
        dispatch(ProductCompareActions.toggleProduct(product, isSelected));
    };

    // isProductSelected function
    const isProductSelected = productId => {
        return selectedProducts.some(p => p.productId === productId);
    };

    // renderCompareToggle function
    const renderCompareToggle = () => {
        // Only show toggle on Category/Nth Level pages and Search Recommendation pages
        const isAllowedPage = source === PAGE_SOURCE.CATEGORY || source === PAGE_SOURCE.SEARCH;

        if (!isAllowedPage) {
            return null;
        }

        if (!isCompareFeatureEnabled) {
            return null;
        }

        return (
            <Flex
                alignItems='center'
                gap={2}
                data-at={Sephora.debug.dataAt('compare_toggle')}
                css={{
                    [mediaQueries.sm]: {
                        height: `${space[4]}px`,
                        width: 'fit-content'
                    }
                }}
            >
                <Flex
                    alignItems='center'
                    css={{
                        [mediaQueries.sm]: {
                            height: `${space[4]}px`,
                            width: 'fit-content',
                            gap: `${space[1]}px`
                        }
                    }}
                >
                    <Icon
                        name='compareToggle'
                        aria-hidden={true}
                        size={22}
                        css={{
                            display: 'block',
                            alignSelf: 'center'
                        }}
                    />
                    <Text
                        is='span'
                        fontSize={'base'}
                        css={{
                            [mediaQueries.sm]: {
                                lineHeight: lineHeights.tight
                            }
                        }}
                    >
                        {compare}
                    </Text>
                </Flex>
                <InputSwitch
                    name='compareToggle'
                    checked={isCompareModeEnabled}
                    onChange={handleCompareToggle}
                    size='16'
                    label={compareTool}
                />
            </Flex>
        );
    };

    // getErrorMessages function
    const getErrorMessages = (searchWarningMessagesProp, refinementsProp, checkedRefinementsProp, countProp) => {
        let errorMessage1 = (searchWarningMessagesProp && searchWarningMessagesProp[0].messages[0].split('.')[0]) || '';
        let errorMessage2 = (searchWarningMessagesProp && searchWarningMessagesProp[0].messages[0].split('.')[1]) || '';

        if (countProp === 0) {
            const isSDDEnabled = Sephora.configurationSettings.isSameDayShippingEnabled;
            const sameDayDelivery = checkedRefinementsProp.find(item => item.indexOf('filters[SameDay]') !== -1);
            const isSDDOnly = countProp === 0 && sameDayDelivery && checkedRefinementsProp.length === 1;
            const isNDDNewName = isNDDNewDeliveryName();

            if (isSDDOnly && !isSDDEnabled) {
                errorMessage1 = isNDDNewName ? NDDUnavailable : SDDUnavailable;
                errorMessage2 = SDDUnavailable2;
            } else {
                let zipCode = '';

                if (isSDDOnly) {
                    zipCode = sameDayDelivery.split('=')[1];
                    const template = isNDDNewName ? noNDDResult : noSDDResult;
                    errorMessage1 = StringUtils.format(template, [zipCode]);
                    errorMessage2 = noSDDResult2;
                } else {
                    errorMessage1 = errorMessage1 ? errorMessage1 + '.' : noResults1;
                    errorMessage2 = errorMessage2 ? errorMessage2 + '.' : noResults2;
                }
            }
        }

        return {
            errorMessage1,
            errorMessage2
        };
    };

    const flagViewableImpression = product => {
        if (product?.productId) {
            firedViewableImpressionIdsRef.current.add(product.productId);
        }
    };

    const checkViewableImpressionFired = product => {
        return product?.productId && firedViewableImpressionIdsRef.current.has(product.productId);
    };

    // increaseServerRenderedMarketingTiles function
    const increaseServerRenderedMarketingTiles = () => {
        serverRenderedMarketingTilesRef.current++;
    };

    // decideSpecialPlacements function
    const decideSpecialPlacements = ({
        baseItemCount,
        isGenAIChatEnabled,
        enableMidPageBanner,
        shouldShowMidPageBanner,
        pageSource,
        sssConfig,
        productRowsCount
    }) => {
        const isMidPageBannerAllowedPage = pageSource === PAGE_SOURCE.CATEGORY || pageSource === PAGE_SOURCE.SEARCH;

        return {
            aiChat: isGenAIChatEnabled ? (baseItemCount <= AI_PRODUCT_CHAT_POSITION ? 'APPEND' : 'POSITIONED') : 'DISABLED',

            midBanner:
                enableMidPageBanner && shouldShowMidPageBanner && isMidPageBannerAllowedPage
                    ? baseItemCount <= MID_BANNER_DEFAULT_POSITION
                        ? 'APPEND'
                        : 'POSITIONED'
                    : 'DISABLED',

            smartSkinScan: sssConfig?.enabled ? 'POSITIONED' : 'DISABLED',

            // SRP-only, eligibility-gated. Control / pending / <3 terms / <2 rows / non-search => DISABLED
            // (reserve nothing, displace nothing). Uses the SAME predicate the shell renders on.
            relatedSearches:
                pageSource === PAGE_SOURCE.SEARCH && isRelatedSearchesEligible(relatedSearchesVariant, relatedSearchTerms, productRowsCount)
                    ? 'POSITIONED'
                    : 'DISABLED'
        };
    };

    // appendDeferredItems function
    const appendDeferredItems = ({ result, placementPolicy }) => {
        if (placementPolicy.aiChat === 'APPEND') {
            result.push({
                type: GRID_TYPES.AI_PRODUCT_CHAT,
                position: result.length
            });
        }

        if (placementPolicy.midBanner === 'APPEND') {
            result.push({
                type: GRID_TYPES.MID_PAGE_BANNER,
                position: result.length
            });
        }

        return result;
    };

    // computeReservedPositions function
    const computeReservedPositions = ({
        slotMap, isSmallGrid, isGenAIChatEnabled, placementPolicy, sssPlacement
    }) => {
        const reserved = new Map();
        let aibcPosition = AI_PRODUCT_CHAT_POSITION;
        const isSSSEnabled = placementPolicy.smartSkinScan === 'POSITIONED';

        if (isSSSEnabled && sssPlacement === SSS_PLACEMENTS.AIBC_SPOT) {
            // Challenger 2: SSS takes AIBC's spot, AIBC moves down
            reserved.set(aibcPosition, GRID_TYPES.SMART_SKIN_SCAN);

            if (isGenAIChatEnabled && placementPolicy.aiChat === 'POSITIONED') {
                aibcPosition = AI_PRODUCT_CHAT_POSITION + SSS_OFFSET_FROM_AIBC;
                reserved.set(aibcPosition, GRID_TYPES.AI_PRODUCT_CHAT);
            }
        } else {
            if (placementPolicy.aiChat === 'POSITIONED') {
                reserved.set(aibcPosition, GRID_TYPES.AI_PRODUCT_CHAT);
            }

            if (isSSSEnabled && sssPlacement === SSS_PLACEMENTS.BELOW_AIBC) {
                // Challenger 1: When AIBC is positioned in-grid, place SSS below it.
                // If AIBC falls back to APPEND, place SSS at AIBC's anchor position instead.
                const isAibcPositioned = isGenAIChatEnabled && placementPolicy.aiChat === 'POSITIONED';
                const sssPosition = isAibcPositioned ? AI_PRODUCT_CHAT_POSITION + SSS_OFFSET_FROM_AIBC : AI_PRODUCT_CHAT_POSITION;

                reserved.set(sssPosition, GRID_TYPES.SMART_SKIN_SCAN);
            }
        }

        if (placementPolicy.midBanner === 'POSITIONED') {
            // On small grids, AI chat spans a full row (2 columns) but isn't counted as a product,
            // so we offset the banner to preserve the intended visual position.
            const offset = isGenAIChatEnabled && isSmallGrid ? 1 : 0;
            reserved.set(MID_BANNER_DEFAULT_POSITION + offset, GRID_TYPES.MID_PAGE_BANNER);
        }

        if (placementPolicy.relatedSearches === 'POSITIONED') {
            // Reserve ADDITIVELY -- slide past any occupied slot (AIBC/SSS/banner/marketing) so the
            // section inserts-and-reflows without displacing another special placement or a product.
            let relatedSearchesPosition = isSmallGrid ? RELATED_SEARCHES_POSITION_SM : RELATED_SEARCHES_POSITION_LG;

            // Slide past slots already taken by another special placement (reserved) OR an incoming
            // marketing tile (slotMap, reserved later in this fn) so neither is overwritten/displaced.
            while (reserved.has(relatedSearchesPosition) || slotMap.has(relatedSearchesPosition)) {
                relatedSearchesPosition++;
            }

            reserved.set(relatedSearchesPosition, GRID_TYPES.RELATED_SEARCHES);
        }

        for (const pos of slotMap.keys()) {
            if (!reserved.has(pos)) {
                reserved.set(pos, GRID_TYPES.MARKETING_TILE);
            }
        }

        return reserved;
    };

    // placeAtPosition function
    const placeAtPosition = ({ position, reserved, queues, placedFlags }) => {
        const forcedType = reserved.get(position);

        // Forced placement
        if (forcedType) {
            if (forcedType === GRID_TYPES.AI_PRODUCT_CHAT && !placedFlags.aiChat) {
                return { type: forcedType, position };
            }

            if (forcedType === GRID_TYPES.MID_PAGE_BANNER && !placedFlags.midBanner) {
                return { type: forcedType, position };
            }

            if (forcedType === GRID_TYPES.SMART_SKIN_SCAN && !placedFlags.smartSkinScan) {
                return { type: forcedType, position };
            }

            if (forcedType === GRID_TYPES.RELATED_SEARCHES && !placedFlags.relatedSearches) {
                return { type: forcedType, position };
            }

            if (forcedType === GRID_TYPES.MARKETING_TILE && queues.marketing.has(position)) {
                return {
                    type: GRID_TYPES.MARKETING_TILE,
                    position,
                    ...queues.marketing.get(position)
                };
            }
        }

        // Fallbacks by priority
        if (queues.products.length > 0) {
            return {
                type: GRID_TYPES.PRODUCT_TILE,
                position,
                ...queues.products[0]
            };
        }

        if (queues.marketing.size > 0) {
            // Get earliest slot first
            const [pos, tile] = [...queues.marketing.entries()].sort(([a], [b]) => a - b)?.[0];

            return {
                type: GRID_TYPES.MARKETING_TILE,
                position: pos,
                ...tile
            };
        }

        return null;
    };

    // buildGrid function
    const buildGrid = ({ productsParam, marketingTilesParam, config }) => {
        const productQueue = [...productsParam];
        const marketingQueue = new Map(marketingTilesParam);
        // Same responsive column count as the placement anchor: 2 cols SMUI (<556px) / 3 cols LGUI.
        const columns = config.isSmallGrid ? 2 : 3;
        const productRowsCount = Math.ceil(productsParam.length / columns);
        const placementPolicy = decideSpecialPlacements({
            baseItemCount: productsParam.length + marketingTilesParam.length,
            productRowsCount,
            ...config
        });

        const reserved = computeReservedPositions({
            slotMap: marketingQueue,
            placementPolicy,
            sssPlacement: config.sssPlacement,
            ...config
        });

        const placedFlags = {
            aiChat: false,
            midBanner: false,
            smartSkinScan: placementPolicy.smartSkinScan === 'DISABLED',
            relatedSearches: placementPolicy.relatedSearches === 'DISABLED'
        };
        const isDone = () =>
            productQueue.length === 0 &&
            marketingQueue.size === 0 &&
            placedFlags.aiChat &&
            placedFlags.midBanner &&
            placedFlags.smartSkinScan &&
            placedFlags.relatedSearches;
        const result = [];
        const MAX_POSITIONS = productsParam.length + marketingQueue.size + reserved.size + 2; // safety for banners

        for (let position = 0; position < MAX_POSITIONS; position++) {
            const placed = placeAtPosition({
                position,
                reserved,
                queues: {
                    products: productQueue,
                    marketing: marketingQueue
                },
                placedFlags
            });

            if (placed) {
                if (placed.type === GRID_TYPES.RELATED_SEARCHES) {
                    placed.productRowsCount = productRowsCount;
                }

                result.push(placed);

                switch (placed.type) {
                    case GRID_TYPES.PRODUCT_TILE:
                        productQueue.shift();

                        break;

                    case GRID_TYPES.MARKETING_TILE:
                        marketingQueue.delete(placed.position);

                        break;

                    case GRID_TYPES.AI_PRODUCT_CHAT:
                        placedFlags.aiChat = true;

                        break;

                    case GRID_TYPES.MID_PAGE_BANNER:
                        placedFlags.midBanner = true;

                        break;

                    case GRID_TYPES.SMART_SKIN_SCAN:
                        placedFlags.smartSkinScan = true;

                        break;

                    case GRID_TYPES.RELATED_SEARCHES:
                        placedFlags.relatedSearches = true;

                        break;

                    default:
                        break;
                }
            }

            // Early exit
            if (isDone()) {
                break;
            }
        }

        return appendDeferredItems({ result, placementPolicy });
    };

    // getMarketingTilePosition function
    const getMarketingTilePosition = (tile, tileIndex) => {
        const marketingTilePosition = tile?.slot ?? marketingTilePositions?.[tileIndex] ?? DEFAULT_MARKETING_TILE_SLOTS[tileIndex];

        if (marketingTilePosition !== undefined) {
            return Number(marketingTilePosition) - 1;
        }

        return tileIndex === 0 ? (isBrand ? 0 : isSearch ? 1 : 7) : tileIndex === 1 ? 15 : 33;
    };

    // getTilesAndMidBanner function
    const getTilesAndMidBanner = (productsParam = [], marketingTilesParam = []) => {
        // There should not be more than 5 tiles on a category page, so a 6th tile shouldn't be shown.
        // https://jira.sephora.com/browse/SMC-2093
        const isMobile = Sephora.isMobile();
        const isSSSEnabled = isMobile && smartSkinScanBannerConfig?.enabled;
        const marketingTilesToRender = marketingTilesParam.slice(0, 5);
        // Map positions using original indices before filtering to preserve slot assignments
        const tilesWithPositions = marketingTilesToRender.map((tile, index) => [getMarketingTilePosition(tile, index), tile]);
        const filteredTilesWithPositions = isSSSEnabled
            ? tilesWithPositions.filter(([, tile]) => tile?.action?.page?.slug !== SKIN_ANALYSIS_TOOL)
            : tilesWithPositions;

        // Kill switch for mid page banner
        const enableMidPageBanner = Sephora?.configurationSettings?.enableMidPageBanner;
        Sephora.logger.verbose('SuperChat: isGenAIChatPLPEnabled', isGenAIChatPLPEnabled);

        return buildGrid({
            productsParam,
            marketingTilesParam: filteredTilesWithPositions,
            config: {
                shouldShowMidPageBanner: showMidPageBanner,
                enableMidPageBanner,
                pageSource: source,
                isSmallGrid: typeof window !== 'undefined' && window.matchMedia('(max-width: 555px)').matches,
                isGenAIChatEnabled: isGenAIChatPLPEnabled,
                sssConfig: isSSSEnabled ? smartSkinScanBannerConfig : null,
                sssPlacement: isSSSEnabled ? smartSkinScanBannerConfig?.placement : null
            }
        });
    };

    // getProductsToDisplay function
    const getProductsToDisplay = (allProducts, { dedicatedSponsorProducts, dedicatedSponsorProductsLoaded, showDedicatedSponsorProducts }) => {
        // Dedicated sponsor products feature enabled: filter catalog-sponsored products first.
        // If the dedicated API /v1/browseSearchProduct is not loaded yet, getProductsWithSponsors may insert
        // skeleton placeholders; once loaded, it merges dedicated sponsor products into grid positions.
        if (showDedicatedSponsorProducts) {
            const nonSponsoredProducts = allProducts.filter(product => !product?.sponsored);
            const result = catalogUtils.getProductsWithSponsors(nonSponsoredProducts, dedicatedSponsorProducts, dedicatedSponsorProductsLoaded);

            return result?.productsWithSponsor;
        }

        // Fallback: use catalog products as-is (may include catalog sponsored products)
        // Catalog response /api/v2/catalog/categories/{cat}/seo (SPA navigation)
        return allProducts;
    };

    // componentDidMount
    useEffect(() => {
        // Load compare state from IndexedDB to hydrate Redux
        dispatch(ProductCompareActions.loadCompareState());

        if (showPurchasedFlagOnGridPages?.challengerOne || showPurchasedFlagOnGridPages?.challengerTwo) {
            const userIdValue = userUtils.getProfileId();

            if (userIdValue && !apiCalled) {
                loadPurchaseHistory();
                setApiCalled(true);
            }
        }

        if (currentPage > 1) {
            UI.scrollToTop();
        }

        digitalData.page.pageInfo.eventPageName = pageName;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fire PLA onload event when showSMNEnabled becomes true (handles Test Target loading after mount)
    useEffect(() => {
        if (showSMNEnabled && !hasFiredPlaOnLoadRef.current) {
            const sponsoredProducts = (products || []).filter(p => p.sponsored);

            if (sponsoredProducts.length > 0) {
                rmnAndPla.firePlaOnLoadEvent(sponsoredProducts);
                hasFiredPlaOnLoadRef.current = true; // Only mark as fired if we had products to track
            }
        }
    }, [showSMNEnabled, products]);

    // componentDidUpdate - handle products change
    useEffect(() => {
        setIsLoading(false);

        if (setResultsCount) {
            setResultsCount(products?.length || 0);
        }

        // Only scroll when navigating backwards (prevPage - 1 === currentPage)
        if (prevPageRef.current - 1 === currentPage && rootRef.current && window.scrollY > rootRef.current.offsetTop) {
            layoutWrapRef?.current?.scrollIntoView({
                block: 'start',
                behavior: 'smooth'
            });
        }

        // Update prevPageRef for next render
        prevPageRef.current = currentPage;
    }, [products, setResultsCount, currentPage, layoutWrapRef]);

    // componentDidUpdate - handle page name change
    useEffect(() => {
        if (digitalData.page.pageInfo.eventPageName !== pageName) {
            digitalData.page.pageInfo.eventPageName = pageName;
        }
    }, [pageName]);

    // componentDidUpdate - handle category name change for PLA tracking
    useEffect(() => {
        // Only clear and allow re-tracking if category actually changed (not on initial mount)
        if (prevCategoryNameForPlaRef.current !== categoryName) {
            firedViewableImpressionIdsRef.current.clear();
            hasFiredPlaOnLoadRef.current = false; // Reset to allow PLA onload event on new category

            // Fires the On Load Tracking Event for the PLAs
            if (showSMNEnabled) {
                const sponsoredProducts = (products || []).filter(p => p.sponsored);

                if (sponsoredProducts.length > 0) {
                    rmnAndPla.firePlaOnLoadEvent(sponsoredProducts);
                    hasFiredPlaOnLoadRef.current = true; // Only mark as fired if we had products to track
                }
            }
        }

        prevCategoryNameForPlaRef.current = categoryName;
    }, [categoryName, products, showSMNEnabled]);

    // GUAR-8124: Fire EDD analytics on page load
    useEffect(() => {
        const deliveryDate = deliveryOptions?.shipToHome?.deliveryDate;

        if (deliveryDate) {
            const eddData = dateUtils.getEddAnalyticsData(deliveryDate);

            if (eddData) {
                processEvent.process(anaConsts.ASYNC_PAGE_LOAD, {
                    data: {
                        eventStrings: [`event320=${eddData.daysDiff}`],
                        eddDate: eddData.eddDate,
                        pageName: digitalData.page.attributes.sephoraPageInfo.pageName,
                        pageType: digitalData.page.category.pageType
                    }
                });
            }
        }

        return () => {
            // GUAR-8124: On un-mount remove eddDate from next calls
            digitalData.page.attributes.eddDate && delete digitalData.page.attributes.eddDate;
        };
    }, [deliveryOptions?.shipToHome?.deliveryDate]);

    // componentDidUpdate - handle purchase history for logged in users
    useEffect(() => {
        if (
            showPurchasedFlagOnGridPages?.challengerOne ||
            showPurchasedFlagOnGridPages?.challengerTwo ||
            (isPurchaseTestConfigured && source === PAGE_SOURCE.SEARCH)
        ) {
            if (profileId && !apiCalled) {
                loadPurchaseHistory();
                setApiCalled(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiCalled, showPurchasedFlagOnGridPages, isPurchaseTestConfigured, profileId]);

    // Render logic
    const count = products.length;
    const styles = getStyles();
    const keysUsedforPickupAndDelivery = ['Ramassage et livraison', 'In Store & Delivery', 'Pickup & Delivery', 'En magasin et pour la livraison'];
    const isDeliveryFilterApplied = keysUsedforPickupAndDelivery.some(key => Array.isArray(appliedFilters[key]) && appliedFilters[key].length > 0);

    const checkedRefinements = catalogUtils.getSelectedRefinements(refinements).checkedIds;
    const errorMessages = getErrorMessages(searchWarningMessages, refinements, checkedRefinements, count);

    const productsToMap = getProductsToDisplay(products, {
        dedicatedSponsorProducts: sponsorProducts,
        dedicatedSponsorProductsLoaded: sponsorProductsLoaded,
        showDedicatedSponsorProducts: showSponsorProducts
    });
    const sortRefinement = refinements?.find(x => x.type === REFINEMENT_TYPES.SORT);
    const selectedOrDefaultOption = sortRefinement ? catalogUtils.getSelectedOrDefaultSortOption(sortRefinement.values, showUpdateDefaultSort) : null;

    const tilesAndMidBanner = getTilesAndMidBanner(productsToMap, marketingTiles);

    return (
        <div ref={rootRef}>
            {h1 && (
                <VisuallyHidden
                    is='h1'
                    children={h1}
                />
            )}
            <Grid
                gap={3}
                marginTop={[null, 0]}
                marginBottom={4}
            >
                <FilterChiclets
                    entry='filter chiclet'
                    showUpperFunnelChiclets
                    refinements={refinements}
                    filters={appliedFilters}
                    onRemoveChiclet={removeFilterValue}
                    onClearAllFilters={clearAllFilters}
                    simplifyFiltersOnPLP={simplifyFiltersOnPLP}
                />
                <Flex
                    flexDirection='column'
                    gap={2}
                >
                    <Flex
                        alignItems='center'
                        justifyContent='space-between'
                    >
                        {renderResultsText({
                            resultsText,
                            searchQuery,
                            pageType
                        })}
                        {count > 0 && (
                            <>
                                <Media lessThan='md'>{renderCompareToggle()}</Media>
                                <Media greaterThan='sm'>
                                    <ProductSort
                                        refinement={sortRefinement}
                                        selectFilters={selectFilters}
                                        selectedOrDefaultOption={selectedOrDefaultOption}
                                    />
                                </Media>
                            </>
                        )}
                    </Flex>
                    {count > 0 && <Media greaterThan='sm'>{renderCompareToggle()}</Media>}
                </Flex>
            </Grid>

            {count > 0 ? (
                <div
                    css={styles.root}
                    {...(isSearch && {
                        'data-cnstrc-search': true
                    })}
                    {...(isBrowse && {
                        'data-cnstrc-browse': true
                    })}
                    {...(totalProducts === 0 && {
                        'data-cnstrc-zero-result': true
                    })}
                    data-cnstrc-num-results={totalProducts}
                    data-cnstrc-result-id={resultId}
                    data-cnstrc-filter-name={constructorFilters.browseFilterName}
                    data-cnstrc-filter-value={constructorFilters.browseFilterValue}
                >
                    {tilesAndMidBanner.map((product, index) => {
                        if (product?.type === GRID_TYPES.AI_PRODUCT_CHAT) {
                            return (
                                <Box
                                    key={`productListPageTile_${prevContextId || 'default'}_${categoryName || 'unknown'}`}
                                    css={[styles.item, styles.productListContainer]}
                                    style={{ order: index }}
                                >
                                    <ProductListPage
                                        products={tilesAndMidBanner}
                                        pageType={pageType}
                                        searchQuery={searchQuery}
                                        giftFinder={giftFinder}
                                    />
                                </Box>
                            );
                        }

                        if (product?.type === GRID_TYPES.SMART_SKIN_SCAN) {
                            return (
                                <Box
                                    key={`smartSkinScan_${index}`}
                                    css={[styles.item, styles.productListContainer]}
                                    style={{ order: index }}
                                >
                                    <Media
                                        lessThan='md'
                                        style={{ marginLeft: -10, marginRight: -10 }}
                                    >
                                        <SmartSkinScanBanner />
                                    </Media>
                                </Box>
                            );
                        }

                        if (product?.type === GRID_TYPES.RELATED_SEARCHES) {
                            return (
                                <Box
                                    key={`relatedSearches_${index}`}
                                    css={styles.relatedSearchesRow}
                                    style={{ order: index }}
                                >
                                    <RelatedSearches productRowsCount={product.productRowsCount} />
                                </Box>
                            );
                        }

                        if (product?.type === GRID_TYPES.MID_PAGE_BANNER) {
                            return (
                                <BannerComponent
                                    {...props}
                                    key={`midPageBanner_${product?.position || index}`}
                                    count={count}
                                    marketingTiles={marketingTiles}
                                    increaseServerRenderedMarketingTiles={increaseServerRenderedMarketingTiles}
                                    source={source}
                                    showMidPageBanner={showMidPageBanner}
                                    position={index || 12}
                                />
                            );
                        }

                        if (product?.type === GRID_TYPES.MARKETING_TILE) {
                            return (
                                <MarketingTileComponent
                                    key={`marketingTile_${product?.sid || index}`}
                                    index={index}
                                    tile={product}
                                    count={count}
                                    increaseServerRenderedMarketingTiles={increaseServerRenderedMarketingTiles}
                                />
                            );
                        }

                        const isLazyLoaded = index >= SERVER_RENDER_LIMIT - serverRenderedMarketingTilesRef.current;
                        const Component = isLazyLoaded ? LazyLoad : ProductTile;

                        return (
                            <div
                                key={`gridItem_${index}`}
                                css={[styles.item, isLazyLoaded && styles.itemLazy]}
                                style={{ order: index }}
                            >
                                <Component
                                    {...(isLazyLoaded && {
                                        component: ProductTile
                                    })}
                                    isLazyLoaded={isLazyLoaded}
                                    source={source}
                                    deliveryOptions={deliveryOptions}
                                    checkedRefinements={checkedRefinements}
                                    contextId={prevContextId}
                                    product={product}
                                    products={products}
                                    isSkeleton={!product?.currentSku}
                                    flagViewableImpression={flagViewableImpression}
                                    checkViewableImpressionFired={checkViewableImpressionFired}
                                    index={index}
                                    categoryName={categoryName}
                                    parentCategoryName={parentCategoryName}
                                    isDeliveryFilterApplied={isDeliveryFilterApplied}
                                    addItemCnstrcData={addItemCnstrcData}
                                    isCompareModeEnabled={isCompareModeEnabled && isCompareFeatureEnabled}
                                    isProductSelected={isProductSelected(product?.productId)}
                                    onProductCompareToggle={handleProductToggle}
                                    showPurchasedTimesSRP={source === PAGE_SOURCE.SEARCH && showPurchasedTimesSRP}
                                />
                            </div>
                        );
                    })}
                    <div css={[styles.item, styles.showMore]}>
                        <Text
                            is='p'
                            color='gray'
                            lineHeight='tight'
                            fontSize={['sm', 'base']}
                            marginBottom={2}
                            children={currentTotalText}
                        />
                        {dontShowMoreProducts || (
                            <Button
                                disabled={isLoading}
                                variant='secondary'
                                css={styles.showMoreButton}
                                onClick={handleLoadMore}
                                children={showMoreProducts}
                            />
                        )}
                    </div>
                </div>
            ) : (
                <Box lineHeight='tight'>
                    <Text
                        is='h2'
                        fontWeight='bold'
                        fontSize={['md', 'lg']}
                        marginBottom='.25em'
                        data-at={Sephora.debug.dataAt('filter_error')}
                        children={errorMessages.errorMessage1}
                    />
                    <Text
                        is='p'
                        fontSize={[null, 'md']}
                        // messaging contains extraneous space before text
                        css={{ whiteSpace: 'normal' }}
                        children={errorMessages.errorMessage2}
                    />
                    <BannerComponent
                        {...props}
                        count={count}
                        marketingTiles={marketingTiles}
                        increaseServerRenderedMarketingTiles={increaseServerRenderedMarketingTiles}
                        source={source}
                        showMidPageBanner={showMidPageBanner}
                        position={1}
                    />
                </Box>
            )}
            <CompareDrawer
                categoryName={categoryName}
                isOpen={selectedProducts.length > 0 && isCompareFeatureEnabled}
            />
        </div>
    );
}

export default wrapFunctionalComponent(ProductGrid, 'ProductGrid');
